import React from 'react';
import { Link } from 'react-router-dom';
import { Battery, Zap, Sun, Shield, Wrench, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <section 
        className="relative h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1761158495585-eac721decf1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVsJTIwcm9vZiUyMGhvbWV8ZW58MHx8fHwxNzcyOTAyODA0fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-900/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight leading-none uppercase text-white mb-6" data-testid="hero-title">
            POWER YOUR <span className="text-primary">FUTURE</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 mb-8 max-w-2xl mx-auto">
            Premium Power Backup & Energy Solutions with Professional Installation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg h-auto" data-testid="shop-now-button">
                Shop Now
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-slate-900 font-bold uppercase tracking-wider px-8 py-6 text-lg h-auto" data-testid="book-service-button">
                Book Service
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight leading-tight uppercase mb-4">
            OUR <span className="text-primary">SOLUTIONS</span>
          </h2>
          <p className="text-lg text-slate-600">Premium equipment for all your power needs</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="group p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-slate-200" data-testid="category-card-inverters">
            <Battery className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-2xl font-heading font-semibold mb-3 uppercase">Inverters & UPS</h3>
            <p className="text-slate-600 mb-6">
              High-performance inverters and uninterruptible power supplies for your home and business.
            </p>
            <Link to="/products?category=Inverters" className="text-primary font-bold uppercase text-sm hover:underline">
              Browse Inverters →
            </Link>
          </Card>

          <Card className="group p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-slate-200" data-testid="category-card-solar">
            <Sun className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-2xl font-heading font-semibold mb-3 uppercase">Solar Systems</h3>
            <p className="text-slate-600 mb-6">
              Complete solar panel installations with batteries for sustainable energy independence.
            </p>
            <Link to="/products?category=Solar Panels" className="text-primary font-bold uppercase text-sm hover:underline">
              Explore Solar →
            </Link>
          </Card>

          <Card className="group p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-slate-200" data-testid="category-card-batteries">
            <Zap className="h-12 w-12 text-primary mb-4" />
            <h3 className="text-2xl font-heading font-semibold mb-3 uppercase">Batteries</h3>
            <p className="text-slate-600 mb-6">
              Lithium-ion, LiFePO4, and lead-acid batteries for vehicles and backup systems.
            </p>
            <Link to="/products?category=Lithium-ion Batteries" className="text-primary font-bold uppercase text-sm hover:underline">
              View Batteries →
            </Link>
          </Card>
        </div>
      </section>

      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight leading-tight uppercase mb-4">
              PROFESSIONAL <span className="text-primary">SERVICES</span>
            </h2>
            <p className="text-lg text-slate-600">Expert technicians at your doorstep</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 border border-slate-200 hover:border-primary/50 transition-all">
              <Wrench className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">Installation</h3>
              <p className="text-slate-600 text-sm">Professional installation of inverters, solar panels, and batteries</p>
            </div>
            <div className="bg-white p-6 border border-slate-200 hover:border-primary/50 transition-all">
              <Shield className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">Maintenance</h3>
              <p className="text-slate-600 text-sm">Regular checkups and preventive maintenance services</p>
            </div>
            <div className="bg-white p-6 border border-slate-200 hover:border-primary/50 transition-all">
              <Phone className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-xl font-semibold mb-2">24/7 Support</h3>
              <p className="text-slate-600 text-sm">Emergency repair services available round the clock</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-8 py-6 h-auto" data-testid="book-service-cta">
                Book a Service
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-orange-500/20 via-red-500/10 to-transparent p-12 border border-slate-200">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-heading font-bold uppercase mb-4">
              WHY CHOOSE US?
            </h2>
            <ul className="space-y-4 text-slate-700">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">✓</span>
                <span>Premium quality products from trusted manufacturers</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">✓</span>
                <span>Certified and experienced technicians</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">✓</span>
                <span>Warranty coverage on all products and services</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold text-xl">✓</span>
                <span>Same-day service availability</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight leading-tight uppercase mb-4">
            VIEW OUR <span className="text-primary">GALLERY</span>
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Explore our completed installations, products, and success stories
          </p>
          <Link to="/gallery">
            <Button className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-8 py-6 h-auto" data-testid="view-gallery-button">
              View Gallery
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;